import { IPromise, copy, equals } from "angular";
import { AssigneeAvatar } from "@gtmhub/assignees";
import { pushOrReplace, unique } from "@gtmhub/util";

type TeamPlusMembersDynamicValue = { type: "teamPlusMembers"; teamId: string; evaluated?: string[] };
type TeamPlusSubTeamsDynamicValue = { type: "teamOrg"; teamId: string; evaluated?: string[] };
type TeamPlusSubTeamsPlusMembersDynamicValue = { type: "teamOrgPlusMembers"; teamId: string; evaluated?: string[] };
export type TeamsPlusMoreDynamicValues = TeamPlusMembersDynamicValue | TeamPlusSubTeamsPlusMembersDynamicValue | TeamPlusSubTeamsDynamicValue;

export function isTeamsPlusMoreDynamicValue(v: IDynamicValue): v is TeamsPlusMoreDynamicValues {
  return v.type === "teamPlusMembers" || v.type === "teamOrg" || v.type === "teamOrgPlusMembers";
}

export type IDynamicValue =
  | { type: "me"; evaluated?: string[] }
  | { type: "myTeams"; evaluated?: string[] }
  | { type: "activeSessions"; evaluated?: string[] }
  | { type: "pastSessions"; evaluated?: string[] }
  | { type: "upcomingSessions"; evaluated?: string[] }
  | { type: "openSessions"; evaluated?: string[] }
  | { type: "inProgressSessions"; evaluated?: string[] }
  | { type: "notSetSessions"; evaluated?: string[] }
  | TeamsPlusMoreDynamicValues;

export type DynamicValueType = IDynamicValue["type"];

export const dynamicValueEquals = (a: IDynamicValue, b: IDynamicValue): boolean => {
  if (isTeamsPlusMoreDynamicValue(a) && isTeamsPlusMoreDynamicValue(b)) {
    return a.type === b.type && a.teamId === b.teamId;
  }

  return a.type === b.type;
};

export const cleanupDynamicValueEvaluated = (dynamicValue: IDynamicValue): IDynamicValue => {
  const { evaluated, ...cleanDynamicValue } = dynamicValue; // eslint-disable-line @typescript-eslint/no-unused-vars
  return cleanDynamicValue;
};

export interface IGridFilterViewBase<Filter extends IGridFilter, Definition extends IGridFilterDefinition, ViewValue> {
  fieldName: string;
  definition: Definition;
  filter: Filter;
  viewValue?: ViewValue;
}

export interface IGridFilterBase<Value> {
  fieldName: string;
  operator: GridFilterOperator;
  value?: Value;
}

export interface IGridFilterDependency {
  fieldName: string;
  values?: GridFilterValue[];
}

interface IGridFilterDefinitionBase<DefaultValue> {
  /**
   * Displays a user-friendly name in the UI
   */
  displayName: string;

  /**
   * Uniquely identifies a filter. It is usually used for constructing a query for REST-layer (V2) APIs
   */
  fieldName: string;

  /**
   * Optionally, specifies a name that will be used when tracking the addition/removal of the filter.
   * If not specified, the display name will be used.
   */
  trackingName?: string;

  /**
   * Optionally, specifies a list of supported operators to choose from in the UI
   */
  supportedOperators?: GridFilterOperator[];

  /**
   * Optionally, specifies the default operator selected in the UI when creating a new filter.
   * The operator should be present in the list of supported operators.
   */
  defaultOperator?: GridFilterOperator;

  /**
   * Optionally, specifies whether the filter is related to a custom field
   */
  isCustomField?: boolean;

  /**
   * Optionally, specifies a default value for the filter when created in the UI
   */
  defaultValue?: DefaultValue;

  /**
   * Optionally, if true, disallows changing the filter value in the UI
   */
  readonly?: boolean;

  /**
   * Optionally, if true, disallows adding a filter with the same field name in the UI
   */
  unique?: boolean;

  /**
   * Optionally, specifies dependency on a filter. When one filter depends on another,
   * the dependent one will only be enabled when the other is present and/or has certain value(s).
   */
  dependsOn?: IGridFilterDependency;
}

export type IArrayGridFilterView = IGridFilterViewBase<IArrayGridFilter, IArrayGridFilterDefinition, IGridFilterTag[]>;

export type GridFilterEntityDataItem = { displayName: string; value: unknown };

export interface IArrayGridFilter extends IGridFilterBase<string[]> {
  dataType: "array";
  dynamicValues?: IDynamicValue[];
  entityData?: GridFilterEntityDataItem[];
}

export interface IArrayGridFilterEntity {
  name: string;
  valueField: string;
  displayNameField: string;
}

interface IArrayGridFilterDefinitionBase extends IGridFilterDefinitionBase<string[]> {
  dataType: "array";

  /**
   * Optionally, limits how many values can be selected
   */
  maxValues?: number;

  /**
   * Optionally, specifies the placeholder for the input
   */
  placeholder?: string;
}

/**
 * Defines a filter which allows selecting values from a static list of allowed ones.
 */
export interface IStaticArrayGridFilterDefinition extends IArrayGridFilterDefinitionBase {
  arrayType: "static";
  entries: GridFilterEntry[];
}

/**
 * Defines an filter which allows selecting values bound to a data source entity. You need to specify
 * the value & display value columns for the entity.
 */
export interface IEntityArrayGridFilterDefinition extends IArrayGridFilterDefinitionBase {
  arrayType: "entity";
  entity: IArrayGridFilterEntity;
}

/**
 * Defines a filter which allows selecting one or more sessions as values.
 */
export interface ISessionArrayGridFilterDefinition extends IArrayGridFilterDefinitionBase {
  arrayType: "session";

  /**
   * Optionally, specifies a session to be displayed at the top
   * in the list of sessions in the session selector
   */
  topDisplayedSessionId?: string;
}

/**
 * Defines a filter which allows selecting one or more tags, assignees, teams, users, roles, goals, or OKRs as values.
 */
export interface IGeneralArrayGridFilterDefinition extends IArrayGridFilterDefinitionBase {
  arrayType: "tag" | "assignee" | "team" | "role" | "goal" | "okr" | "user";
}

export type IArrayGridFilterDefinition =
  | IGeneralArrayGridFilterDefinition
  | IEntityArrayGridFilterDefinition
  | IStaticArrayGridFilterDefinition
  | ISessionArrayGridFilterDefinition;

export type ArrayFilterType = IArrayGridFilterDefinition["arrayType"];

export type ITextGridFilterView = IGridFilterViewBase<ITextGridFilter, ITextGridFilterDefinition, null>;

export interface ITextGridFilter extends IGridFilterBase<string> {
  dataType: "text";
}

export interface ITextGridFilterDefinition extends IGridFilterDefinitionBase<string> {
  dataType: "text";
  placeholder?: string;
}

export interface IBooleanGridFilter extends IGridFilterBase<boolean> {
  dataType: "boolean";
}

export interface IBooleanGridFilterDefinition extends IGridFilterDefinitionBase<boolean> {
  dataType: "boolean";
  entries: GridBooleanFilterEntry[];

  // applies the first defined filter entry from the filter `entries` array when created
  applyFirstOnCreate?: boolean;

  // infers on page load the persisted filter entry value from the operator and ignores the filter value itself
  inferValueFromOperator?: boolean;
}

export type INumericGridFilterView = IGridFilterViewBase<INumericGridFilter, INumericGridFilterDefinition, number | null>;

export interface INumericGridFilter extends IGridFilterBase<number | INumericRange> {
  dataType: "numeric";
}

export interface INumericGridFilterDefinition extends IGridFilterDefinitionBase<number | INumericRange> {
  dataType: "numeric";
  percentage?: boolean;
}

export interface IDateRangeViewValue {
  isSelectingCustomDateRange: boolean;
  dateRange: IDateRange | string;
}

export type IDateGridFilterView = IGridFilterViewBase<IDateGridFilter, IDateGridFilterDefinition, string | IDateRangeViewValue>;

export type GridFilterPredefinedDateRange = { displayName: string; value: string };

export interface IDateGridFilter extends IGridFilterBase<string | IDateRange> {
  dataType: "date";
}

export interface IDateGridFilterDefinition extends IGridFilterDefinitionBase<string | IDateRange> {
  dataType: "date";
  predefinedDateRanges?: GridFilterPredefinedDateRange[];
  local?: boolean;
  minDate?: string;
  maxDate?: string;
}

export type GridFilterEntry = string | { displayName: string; avatar?: TagAvatar; value: string | number | boolean };

export type GridBooleanFilterEntry = { displayName: string; value: boolean; operator?: GridFilterOperator };

export const entryValue = (entry: GridFilterEntry): string | number | boolean => (typeof entry === "string" ? entry : entry.value);

export const entryDisplayName = (entry: GridFilterEntry): string => (typeof entry === "string" ? entry : entry.displayName);

export type IEnumGridFilterView = IGridFilterViewBase<IEnumGridFilter, IEnumGridFilterDefinition, GridFilterEntry>;

export interface IEnumGridFilter extends IGridFilterBase<string | string[]> {
  dataType: "enum";
}

export interface IEnumGridFilterDefinition extends IGridFilterDefinitionBase<string> {
  dataType: "enum";
  entries: GridFilterEntry[];
}

export type IGridFilter = IArrayGridFilter | ITextGridFilter | IBooleanGridFilter | INumericGridFilter | IEnumGridFilter | IDateGridFilter;

export type OrFiltersGroup = IGridFilter[];
export type OrArrayFiltersGroup = IArrayGridFilter[];

export type GridFilterValue = IGridFilter["value"];

export type IGridFilterView = IGridFilterViewBase<IGridFilter, IGridFilterDefinition, unknown>;
export type OrGridFiltersGroupView = IGridFilterView[];

export type IGridFilterDefinition =
  | IArrayGridFilterDefinition
  | ITextGridFilterDefinition
  | IBooleanGridFilterDefinition
  | INumericGridFilterDefinition
  | IEnumGridFilterDefinition
  | IDateGridFilterDefinition;

export type GridFilterDefinitions = Record<string, IGridFilterDefinition>;

export type GridFilterDataType = IGridFilter["dataType"];

export interface IDateRange {
  startDate: string;
  endDate: string;
}

export interface INumericRange {
  from: number;
  to: number;
}

export type GridFilterOperator =
  | "equals"
  | "notEqual"
  | "isOneOf"
  | "isNotOneOf"
  | "contains"
  | "doesNotContain"
  | "isBetween"
  | "isNotBetween"
  | "greater"
  | "less"
  | "isBefore"
  | "isAfter"
  | "missingOrEmpty";

export const isPositiveOperator = (operator: GridFilterOperator): boolean =>
  (<GridFilterOperator[]>["notEqual", "isNotOneOf", "doesNotContain", "isNotBetween"]).every((negativeOperator) => negativeOperator !== operator);

export const isRangeOperator = (operator: GridFilterOperator): boolean => operator === "isBetween" || operator === "isNotBetween";

export const isMissingOrEmptyOperator = (operator: GridFilterOperator): boolean => operator === "missingOrEmpty";

export const expandFilterValue = (filter: IArrayGridFilter): string[] => {
  const evaluatedDynamicValues = (filter.dynamicValues || []).reduce<string[]>((values, dynamicValue) => [...values, ...dynamicValue.evaluated], []);
  return unique([...(filter.value || []), ...evaluatedDynamicValues]);
};

export type ItemType = "Objective" | "Key Result" | "Task" | "Employee" | "Team";

export type TagAvatar = AssigneeAvatar | { color: string } | { emoji: string } | { gtmhub: boolean };

export interface IGridFilterTag {
  id: string;
  name: string;
  avatar?: TagAvatar;
  dynamicValue?: IDynamicValue;
  isActive?: boolean;
}

export type QueryFn = (input: string, definition?: IArrayGridFilterDefinition, filter?: IArrayGridFilter) => IGridFilterTag[] | IPromise<IGridFilterTag[]>;

export interface IArrayGridFilterDataLoader {
  placeholder: string;
  query: QueryFn;
}

export const hasFilterValue = (filter: IGridFilter): boolean => {
  const hasDynamicValues = filter.dataType === "array" && (filter.dynamicValues || []).length > 0;
  if (hasDynamicValues) {
    return true;
  }

  if (filter.operator === "missingOrEmpty") {
    return true;
  }

  const value = filter.value;
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (filter.dataType === "date") {
    return value && (typeof value !== "object" || !!(value as IDateRange).startDate);
  }

  return value === 0 || value === false || !!value;
};

export const hasSelectedFilterValue = (filter: IGridFilter, value: GridFilterValue): boolean => {
  if (filter.dataType !== "array") {
    return equals(filter.value, value);
  }
  if (hasValue(filter.value, value)) {
    return true;
  }
  return filter.dynamicValues?.some((values) => hasValue(values.evaluated, value));
};

function hasValue(values: string[] | undefined, value: GridFilterValue): boolean {
  return values?.some((v) => (Array.isArray(value) ? value.includes(v) : value === v));
}

export const createFilterDefaultValue = (definition: IGridFilterDefinition, operator?: GridFilterOperator): GridFilterValue => {
  if (!operator) {
    operator = definition.defaultOperator;
  }

  if (typeof definition.defaultValue !== "undefined") {
    return copy(definition.defaultValue);
  }

  if (definition.dataType === "array") {
    return [];
  }

  if (definition.dataType === "date" && isRangeOperator(operator)) {
    return { startDate: undefined, endDate: undefined };
  }

  return undefined;
};

export const createFilter = (definition: IGridFilterDefinition): IGridFilter => ({
  dataType: definition.dataType,
  fieldName: definition.fieldName,
  operator: definition.defaultOperator,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: createFilterDefaultValue(definition) as any,
});

/**
 * Removes a value from all grid filters, specified by fieldName, in every filters group and returns the modified filters groups
 * @param filters filters groups to be modified
 * @param args specified field name, value to be removed, create new filter method
 * @returns the modified filters groups with all their specified filters lacking the args value passed
 */
export function removeValueFromFiltersInEveryGroup(
  filters: OrFiltersGroup[],
  args: { fieldName: string; value: string; createFilter(value: string[]): IArrayGridFilter }
): OrFiltersGroup[] {
  return filters.map((filtersGroup) => {
    return filtersGroup.map((filter) => {
      if (filter.fieldName === args.fieldName) {
        const newFilterValue = expandFilterValue(<IArrayGridFilter>filter).filter((value) => value !== args.value);
        return args.createFilter(newFilterValue);
      }

      return filter;
    });
  });
}

/**
 * Adds a value to all grid filters, specified by fieldName, in every filters group, or creates a new filter with the fieldName given containing the value if the group has no such filters, and returns the modified filters groups
 * @param filters filters groups to be modified
 * @param args specified field name, value to be added, create new filter method
 * @returns the modified filters groups, containing at least 1 filter, specified by fieldName, with all their specified filters having the args value passed
 */
export function addValueToExistingFiltersOrCreateNewFilterInEveryGroup(
  filters: OrFiltersGroup[],
  args: { fieldName: string; value: string; createFilter(value: string[]): IArrayGridFilter }
): OrFiltersGroup[] {
  return filters.map((filtersGroup) => {
    if (!filtersGroup.some((filter) => filter.fieldName === args.fieldName)) {
      return pushOrReplace(filtersGroup, args.createFilter([args.value]));
    }

    return filtersGroup.map((filter) => {
      if (filter.fieldName === args.fieldName) {
        const newFilterValue = expandFilterValue(<IArrayGridFilter>filter);
        if (!newFilterValue.includes(args.value)) {
          newFilterValue.push(args.value);

          return args.createFilter(newFilterValue);
        }
      }

      return filter;
    });
  });
}

/**
 * Creates and returns a Set containing the identifiers of all values that are contained in all target filters in every filters group
 * @param filters filters groups to be iterated
 * @param args the field name of the filters we are checking for values
 * @returns a Set containing the identifiers of all values that are contained in all target filters in every filters group
 */
export function getCommonValuesUsedInAllFiltersInEveryGroup(filters: OrFiltersGroup[], args: { fieldName: string }): Set<string> {
  const selectionSet = new Set<string>();
  const hasTargetFiltersInAllGroups = filters.every((filtersGroup) => filtersGroup.some((filter) => filter.fieldName === args.fieldName));

  if (hasTargetFiltersInAllGroups) {
    const targetFiltersValues: string[][] = filters.reduce((targetValuesArr: string[][], filtersGroup: OrFiltersGroup) => {
      const targetFiltersInGroup = <OrArrayFiltersGroup>filtersGroup.filter((filter) => filter.fieldName === args.fieldName);
      return [...targetValuesArr, ...targetFiltersInGroup.map((filter) => expandFilterValue(filter))];
    }, []);

    const targetsUsageMap: Map<string, number> = targetFiltersValues.reduce((usageMap: Map<string, number>, targetValues: string[]) => {
      targetValues.forEach((targetId) => {
        const usageCount = usageMap.get(targetId) || 0;
        usageMap.set(targetId, usageCount + 1);
      });
      return usageMap;
    }, new Map<string, number>());

    const totalTargetFiltersCount = targetFiltersValues.length;
    targetsUsageMap.forEach((usageCount: number, targetId: string) => {
      if (usageCount === totalTargetFiltersCount) {
        selectionSet.add(targetId);
      }
    });
  }

  return selectionSet;
}

export function removeFiltersByFieldNameAndEmptyGroups(filters: OrFiltersGroup[], fieldName: string): OrFiltersGroup[] {
  return removeEmptyGroupsFromFilters(removeFiltersByFieldNameFromAllGroups(filters, fieldName));
}

function removeFiltersByFieldNameFromAllGroups(filters: OrFiltersGroup[], fieldName: string): OrFiltersGroup[] {
  return filters.map((filtersGroup) => filtersGroup.filter((filter) => filter.fieldName !== fieldName));
}

function removeEmptyGroupsFromFilters(filters: OrFiltersGroup[]): OrFiltersGroup[] {
  return filters.filter((filtersGroup) => !!filtersGroup.length);
}

export interface IGridFiltersChange {
  initial: boolean;
}

export interface IAssigneeTagOptions {
  includeSubteamsAndMembers: boolean;
}
